import { defineStore } from 'pinia'
import { ref } from 'vue'
import { toast } from 'vue3-toastify'
import ContactService from '@/services/contact.service'
import type { Contact, ContactListParams, ContactForm, ImportSummary } from '@/types/contact'

export const useContactsStore = defineStore('contacts', () => {
  const contacts = ref<Contact[]>([])
  const isLoading = ref<boolean>(false)
  const listParams = ref<ContactListParams>({
    page: 1,
    per_page: 10,
    filter: { search: '', contact_type: '' },
    sort: '-created_at'
  })

  const isCreateModalOpen = ref<boolean>(false)
  const isEditModalOpen = ref<boolean>(false)
  const isImportModalOpen = ref<boolean>(false)
  const selectedContact = ref<Contact | null>(null)

  const paginationMeta = ref<{
    total: number
    current_page: number
    per_page: number
  } | null>(null)

  const index = async (): Promise<void> => {
    isLoading.value = true
    try {
      const { data } = await ContactService.index(listParams.value)
      const paginatedData = data.data
      
      contacts.value = paginatedData.data
      paginationMeta.value = {
        total: paginatedData.total,
        current_page: paginatedData.current_page,
        per_page: paginatedData.per_page
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch contacts.')
    } finally {
      isLoading.value = false
    }
  }

  const storeContact = async (form: ContactForm): Promise<void> => {
      try {
        await ContactService.store(form)
        toast.success('Contact created successfully.')
        closeCreateModal()
      } catch (error: any) {
        throw error
      }
    }

    const updateContact = async (id: number, form: ContactForm): Promise<void> => {
      try {
        await ContactService.update(id, form)
        toast.success('Contact updated successfully.')
        closeEditModal()
      } catch (error: any) {
        throw error
      }
    }

  const destroyContact = async (id: number): Promise<void> => {
    try {
      await ContactService.destroy(id)
      toast.success('Contact deleted.')
      await index()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete contact.')
    }
  }

  const importContacts = async (file: File): Promise<any> => {
    try {
      const { data } = await ContactService.import(file)
      return data
    } catch (error: any) {
      throw error
    }
  }

  const openCreateModal = (): void => {
    isCreateModalOpen.value = true
  }

  const closeCreateModal = (): void => {
    isCreateModalOpen.value = false
    selectedContact.value = null
  }

  const openEditModal = (contact: Contact): void => {
    selectedContact.value = contact
    isEditModalOpen.value = true
  }

  const closeEditModal = (): void => {
    isEditModalOpen.value = false
    selectedContact.value = null
  }

  const openImportModal = (): void => {
    isImportModalOpen.value = true
  }

  const closeImportModal = (): void => {
    isImportModalOpen.value = false
  }

const form = ref<ContactForm>({
  full_name: '',
  email: '',
  phone: '',
  source: '',
  contact_type: '',
  job_title: '',
  address: '',
  notes: ''
})

const resetForm = (): void => {
  form.value = {
    full_name: '',
    email: '',
    phone: '',
    source: '',
    contact_type: '',
    job_title: '',
    address: '',
    notes: ''
  }
}

  return {
    contacts,
    isLoading,
    listParams,
    isCreateModalOpen,
    isEditModalOpen,
    isImportModalOpen,
    selectedContact,
    paginationMeta,
    form,
    index,
    resetForm,
    storeContact,
    updateContact,
    destroyContact,
    importContacts,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openImportModal,
    closeImportModal
  }
})